import React, { useState, useEffect } from 'react';
import { Calendar, Clock, X, RefreshCw, Video } from 'lucide-react';

const API = 'https://talentflow-backend-production-09dd.up.railway.app/api';

const TIME_SLOTS = ['9:00-10:00 AM','10:00-11:00 AM','11:00-12:00 PM','12:00-1:00 PM','1:00-2:00 PM','2:00-3:00 PM','3:00-4:00 PM','4:00-5:00 PM','5:00-6:00 PM'];

export default function ClientInterviews({ token, requirements }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [scheduleModal, setScheduleModal] = useState(null);
  const [actionModal, setActionModal] = useState(null);
  const [schedDate, setSchedDate] = useState(null);
  const [schedSlots, setSchedSlots] = useState([]);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { loadAllCandidates(); }, []);

  const loadAllCandidates = async () => {
    setLoading(true);
    try {
      const results = await Promise.all(
        requirements.map(req =>
          fetch(`${API}/candidates/${req._id}`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.json())
            .then(d => (d.success ? (d.candidates || []).map(c => ({ ...c, requirementTitle: req.title })) : []))
            .catch(() => [])
        )
      );
      setCandidates(results.flat().filter(c => c.status === 'shortlisted'));
    } catch {}
    setLoading(false);
  };

  const submitSchedule = async () => {
    if (!schedDate || schedSlots.length === 0) return;
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/candidates/${scheduleModal._id}/schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ date: schedDate.toISOString().split('T')[0], slots: schedSlots })
      });
      const d = await r.json();
      if (d.success) {
        setMsg('✅ Interview slots sent to vendor!');
        setScheduleModal(null);
        setSchedDate(null);
        setSchedSlots([]);
        loadAllCandidates();
      }
    } catch {}
    setSubmitting(false);
  };

  const handleAction = async (candidateId, action) => {
    setSubmitting(true);
    try {
      const endpoint = action === 'cancel' ? 'cancel' : 'reschedule';
      const r = await fetch(`${API}/candidates/${candidateId}/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reason })
      });
      const d = await r.json();
      if (d.success) {
        if (action === 'reschedule') {
          setActionModal(null);
          setReason('');
          await loadAllCandidates();
          setScheduleModal(actionModal);
          setMsg('🔄 Rescheduled! Now select new slots for vendor.');
        } else {
          setMsg('❌ Interview cancelled successfully!');
          setActionModal(null);
          setReason('');
          loadAllCandidates();
        }
      }
    } catch {}
    setSubmitting(false);
  };

  const toggleSlot = (slot) => {
    if (schedSlots.includes(slot)) setSchedSlots(schedSlots.filter(s => s !== slot));
    else if (schedSlots.length < 3) setSchedSlots([...schedSlots, slot]);
  };

  const dates = Array.from({length:10},(_,i)=>{
    const d=new Date(); d.setDate(d.getDate()+i); return d;
  }).filter(d=>d.getDay()!==0&&d.getDay()!==6);

  const getStatusBadge = (c) => {
    if (c.scheduleStatus === 'confirmed') return { label: '✅ Confirmed', bg: '#dcfce7', color: '#16a34a' };
    if (c.scheduleStatus === 'pending_vendor') return { label: '⏳ Awaiting Vendor', bg: '#fef9c3', color: '#ca8a04' };
    if (c.scheduleStatus === 'cancelled') return { label: '❌ Cancelled', bg: '#fee2e2', color: '#dc2626' };
    return { label: '📋 Not Scheduled', bg: '#f1f5f9', color: '#64748b' };
  };

  const colors = ['#6366f1','#8b5cf6','#ec4899','#f59e0b','#10b981'];

  if (loading) return <div style={{textAlign:'center',padding:'60px',color:'#94a3b8'}}>Loading interviews...</div>;

  return (
    <div>
      {msg && <div style={{padding:'12px 16px',background:'#dcfce7',color:'#16a34a',borderRadius:'8px',marginBottom:'16px',fontSize:'14px'}}>{msg}<button onClick={()=>setMsg('')} style={{float:'right',background:'none',border:'none',cursor:'pointer',color:'#16a34a'}}>✕</button></div>}

      {candidates.length === 0 ? (
        <div style={{textAlign:'center',padding:'60px',background:'white',borderRadius:'12px',border:'1px solid #e2e8f0',color:'#94a3b8'}}>
          <Calendar size={48} color="#e2e8f0" style={{margin:'0 auto 16px',display:'block'}}/>
          <p style={{margin:'0 0 8px',fontSize:'16px',fontWeight:'500'}}>No shortlisted candidates yet</p>
          <p style={{margin:0,fontSize:'13px'}}>Shortlist candidates from the Candidates section to schedule interviews</p>
        </div>
      ) : (
        <div style={{display:'grid',gap:'16px'}}>
          {candidates.map(c => {
            const badge = getStatusBadge(c);
            const avatarColor = colors[c.name?.charCodeAt(0) % colors.length];
            return (
              <div key={c._id} style={{background:'white',borderRadius:'12px',padding:'20px',border:'1px solid #e2e8f0',boxShadow:'0 1px 4px rgba(0,0,0,0.04)'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'14px'}}>
                  <div style={{display:'flex',gap:'12px',alignItems:'center'}}>
                    <div style={{width:'44px',height:'44px',borderRadius:'50%',background:avatarColor,display:'flex',alignItems:'center',justifyContent:'center',color:'white',fontWeight:'700',fontSize:'16px',flexShrink:0}}>
                      {c.name?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h4 style={{color:'#1e293b',fontWeight:'700',fontSize:'15px',margin:'0 0 4px'}}>{c.name}</h4>
                      <p style={{color:'#64748b',fontSize:'13px',margin:0}}>{c.requirementTitle} • {c.experience} yrs exp</p>
                    </div>
                  </div>
                  <span style={{padding:'4px 12px',borderRadius:'20px',fontSize:'12px',fontWeight:'600',background:badge.bg,color:badge.color}}>{badge.label}</span>
                </div>

                {/* Interview details */}
                {c.scheduleStatus === 'confirmed' && (
                  <div style={{background:'#f0fdf4',borderRadius:'10px',padding:'14px',marginBottom:'14px',border:'1px solid #86efac'}}>
                    <div style={{display:'flex',gap:'20px',flexWrap:'wrap'}}>
                      <span style={{color:'#16a34a',fontSize:'13px',fontWeight:'600'}}><Calendar size={13} style={{verticalAlign:'middle'}}/> {c.interviewDate}</span>
                      <span style={{color:'#16a34a',fontSize:'13px',fontWeight:'600'}}><Clock size={13} style={{verticalAlign:'middle'}}/> {c.confirmedSlot}</span>
                    </div>
                  </div>
                )}

                {c.scheduleStatus === 'pending_vendor' && (
                  <div style={{background:'#fef9c3',borderRadius:'10px',padding:'14px',marginBottom:'14px',border:'1px solid #fde68a'}}>
                    <p style={{color:'#92400e',fontSize:'13px',margin:'0 0 6px',fontWeight:'600'}}>Slots sent to vendor — awaiting confirmation</p>
                    <p style={{color:'#92400e',fontSize:'12px',margin:0}}>Date: {c.interviewDate} | Slots: {(c.interviewSlots||[]).join(', ')}</p>
                  </div>
                )}

                {/* Action buttons */}
                <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
                  {/* Schedule/Reschedule */}
                  {(!c.scheduleStatus || c.scheduleStatus === 'none' || c.scheduleStatus === 'cancelled') && (
                    <button onClick={()=>setScheduleModal(c)} style={{display:'inline-flex',alignItems:'center',gap:'6px',padding:'6px 14px',background:'#0f172a',color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'500',fontFamily:'inherit'}}>
                      <Calendar size={14}/> Schedule Interview
                    </button>
                  )}

                  {c.scheduleStatus === 'pending_vendor' && (
                    <button onClick={()=>setActionModal({...c,action:'reschedule'})} style={{display:'inline-flex',alignItems:'center',gap:'6px',padding:'6px 14px',background:'white',color:'#374151',border:'1px solid #e5e7eb',borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'500',fontFamily:'inherit'}}>
                      <RefreshCw size={14}/> Reschedule
                    </button>
                  )}

                  {c.scheduleStatus === 'confirmed' && (
                    <>
                      {c.meetingLink && (
                        <a href={c.meetingLink} target="_blank" rel="noreferrer" style={{display:'inline-flex',alignItems:'center',gap:'6px',padding:'8px 16px',background:'linear-gradient(135deg,#6366f1,#8b5cf6)',color:'white',borderRadius:'8px',textDecoration:'none',fontSize:'13px',fontWeight:'600'}}>
                          <Video size={14}/> Join Interview
                        </a>
                      )}
                      <button onClick={()=>setActionModal({...c,action:'reschedule'})} style={{display:'inline-flex',alignItems:'center',gap:'6px',padding:'6px 14px',background:'white',color:'#374151',border:'1px solid #e5e7eb',borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'500',fontFamily:'inherit'}}>
                        <RefreshCw size={14}/> Reschedule
                      </button>
                    </>
                  )}

                  {c.scheduleStatus && c.scheduleStatus !== 'none' && c.scheduleStatus !== 'cancelled' && (
                    <button onClick={()=>setActionModal({...c,action:'cancel'})} style={{display:'inline-flex',alignItems:'center',gap:'6px',padding:'6px 14px',background:'white',color:'#dc2626',border:'1px solid #fecaca',borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'500',fontFamily:'inherit'}}>
                      <X size={14}/> Cancel
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Schedule Modal */}
      {scheduleModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}}>
          <div style={{background:'white',borderRadius:'16px',padding:'28px',maxWidth:'520px',width:'100%',boxShadow:'0 40px 80px rgba(0,0,0,0.2)',maxHeight:'90vh',overflowY:'auto'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'16px'}}>
              <h3 style={{color:'#1e293b',fontSize:'16px',fontWeight:'700',margin:0}}>📅 Schedule Interview — {scheduleModal.name}</h3>
              <button onClick={()=>setScheduleModal(null)} style={{background:'none',border:'none',cursor:'pointer',color:'#94a3b8',fontSize:'20px'}}>✕</button>
            </div>
            <div style={{marginBottom:'16px'}}>
              <label style={{color:'#64748b',fontSize:'11px',fontWeight:'600',display:'block',marginBottom:'8px'}}>SELECT DATE</label>
              <div style={{display:'flex',gap:'6px',flexWrap:'wrap'}}>
                {dates.map(d=>(
                  <div key={d.toISOString()} onClick={()=>setSchedDate(d)}
                    style={{padding:'7px 12px',borderRadius:'8px',cursor:'pointer',fontSize:'12px',fontWeight:'600',
                      background:schedDate?.toDateString()===d.toDateString()?'linear-gradient(135deg,#6366f1,#8b5cf6)':'#f8fafc',
                      color:schedDate?.toDateString()===d.toDateString()?'white':'#1e293b',
                      border:schedDate?.toDateString()===d.toDateString()?'none':'1px solid #e2e8f0'}}>
                    {d.toLocaleDateString('en-IN',{weekday:'short',day:'numeric',month:'short'})}
                  </div>
                ))}
              </div>
            </div>
            {schedDate && (
              <div style={{marginBottom:'20px'}}>
                <label style={{color:'#64748b',fontSize:'11px',fontWeight:'600',display:'block',marginBottom:'8px'}}>SELECT UP TO 3 SLOTS ({schedSlots.length}/3)</label>
                <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'8px'}}>
                  {TIME_SLOTS.map(slot=>(
                    <div key={slot} onClick={()=>toggleSlot(slot)}
                      style={{padding:'10px',borderRadius:'8px',cursor:'pointer',fontSize:'11px',fontWeight:'600',textAlign:'center',
                        background:schedSlots.includes(slot)?'linear-gradient(135deg,#6366f1,#8b5cf6)':'#f8fafc',
                        color:schedSlots.includes(slot)?'white':'#1e293b',
                        border:schedSlots.includes(slot)?'none':'1px solid #e2e8f0',
                        opacity:!schedSlots.includes(slot)&&schedSlots.length>=3?0.4:1}}>
                      {slot}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={{display:'flex',gap:'10px'}}>
              <button onClick={()=>setScheduleModal(null)} style={{flex:1,padding:'11px',background:'#f1f5f9',color:'#64748b',border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontWeight:'600'}}>Cancel</button>
              <button onClick={submitSchedule} disabled={!schedDate||schedSlots.length===0||submitting}
                style={{flex:2,padding:'11px',background:'#0f172a',color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontFamily:'inherit',fontWeight:'600',opacity:(!schedDate||schedSlots.length===0)?0.5:1}}>
                {submitting?'Sending...':'Send Slots to Vendor →'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule/Cancel Modal */}
      {actionModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}}>
          <div style={{background:'white',borderRadius:'16px',padding:'32px',maxWidth:'400px',width:'100%',boxShadow:'0 40px 80px rgba(0,0,0,0.2)'}}>
            <h3 style={{color:'#1e293b',fontSize:'18px',fontWeight:'700',margin:'0 0 8px'}}>
              {actionModal.action==='reschedule'?'🔄 Reschedule Interview':'❌ Cancel Interview'}
            </h3>
            <p style={{color:'#64748b',fontSize:'13px',margin:'0 0 20px'}}>
              {actionModal.action==='reschedule'?'Vendor will be notified and you can select new slots.':'This action will cancel the interview. Both parties will be notified.'}
            </p>
            <div style={{marginBottom:'20px'}}>
              <label style={{color:'#64748b',fontSize:'12px',fontWeight:'600',display:'block',marginBottom:'6px'}}>REASON *</label>
              <textarea value={reason} onChange={e=>setReason(e.target.value)} placeholder="Please provide a reason..."
                style={{width:'100%',padding:'10px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px',outline:'none',resize:'none',height:'80px',boxSizing:'border-box',fontFamily:'inherit'}}/>
            </div>
            <div style={{display:'flex',gap:'10px'}}>
              <button onClick={()=>{setActionModal(null);setReason('');}} style={{flex:1,padding:'11px',background:'#f1f5f9',color:'#64748b',border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontWeight:'600'}}>Back</button>
              <button onClick={()=>handleAction(actionModal._id, actionModal.action)} disabled={!reason||submitting}
                style={{flex:1,padding:'11px',background:actionModal.action==='reschedule'?'linear-gradient(135deg,#f59e0b,#d97706)':'linear-gradient(135deg,#ef4444,#dc2626)',color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontWeight:'600',opacity:!reason?0.5:1}}>
                {submitting?'Processing...':(actionModal.action==='reschedule'?'🔄 Reschedule':'❌ Cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
